<template>
	<div class="time-entry-quick-actions">
		<Transition name="time-entry-show">
			<TimeEntryActionsAddEntry
				v-if="showTimeEntryInput"
				class="time-entry-quick-actions__entry"
				:task-id="taskId"
				@close="onTimeEntryCancel()"
			/>
		</Transition>

		<div
			v-if="!showTimeEntryInput && showButtons"
			class="time-entry-quick-actions__buttons"
		>
			<SuiButton
				v-tooltip="$t('addTimeEntry')"
				class="time-entry-quick-actions__button"
				variant="icon"
				:aria-label="$t('addTimeEntry')"
				@click="onAddTimeEntryClick()"
			>
				<SuiIcon name="icon-stopwatch-add-16" />
			</SuiButton>

			<SuiButton
				v-if="allowStartTracker"
				v-tooltip="$t('timeTracker_startTracking')"
				class="time-entry-quick-actions__button"
				:class="{
					'time-entry-quick-actions__button--active':
						isTimeTrackerRunning,
				}"
				:is-disabled="isTimeTrackerRunning"
				:aria-label="$t('timeTracker_startTracking')"
				variant="icon"
				@click="onTimeTrackerStart()"
			>
				<SuiIcon name="icon-play-16" />
			</SuiButton>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useMfeUtils } from '@/composables';
import { type Task } from '@/generated/graphql';
import { SuiButton, SuiIcon } from '@developers/scoro-ui';
import { ref } from 'vue';
import TimeEntryActionsAddEntry from './TimeEntryActionsAddEntry.vue';
import { DEFAULT_TIME_ENTRY_SECONDS } from '../../constants';

export type TimeEntryQuickActionsProps = {
	taskId: Task['id'];
	showButtons?: boolean;
	allowQuickAdd?: boolean;
	allowStartTracker?: boolean;
};

const props = defineProps<TimeEntryQuickActionsProps>();

const { rootEmit } = useMfeUtils();

const isTimeTrackerRunning = ref(false);

const onTimeTrackerStart = () => {
	isTimeTrackerRunning.value = true;
	rootEmit('start-time-tracker', {
		taskId: props.taskId,
	});

	setTimeout(() => {
		isTimeTrackerRunning.value = false;
	}, 2000);
};

const showTimeEntryInput = ref(false);

const onAddTimeEntryClick = async () => {
	if (props.allowQuickAdd) {
		showTimeEntryInput.value = true;
	} else {
		rootEmit('modify-time-entry', {
			taskId: props.taskId,
			duration: DEFAULT_TIME_ENTRY_SECONDS,
			isCompleted: true,
		});
	}
};

const onTimeEntryCancel = () => {
	showTimeEntryInput.value = false;
};
</script>

<style lang="scss">
.time-entry-quick-actions {
	position: relative;
	width: 100%;
	height: 4rem;

	&__entry {
		position: absolute;
	}

	&__buttons {
		display: grid;
		grid-template-columns: repeat(2, 4rem);
		justify-content: center;
	}

	&__button.time-entry-quick-actions__button {
		--sui-button-hover-background-color: #{$neutral-20};
		--sui-button-h-padding: 1rem;

		transition: all 0.2s ease;

		&--active {
			--sui-button-icon-color: #{$green-80};
			--sui-button-hover-background-color: #{$green-10};
			--sui-button-background-color: #{$green-10};
			--sui-button-shadow: 0 0 0 1px #{$green-30} inset;

			&:disabled {
				opacity: 1;
			}
		}
	}
}

.time-entry-show-enter-active {
	transition: all 0.2s ease-in-out;
}

.time-entry-show-enter-from {
	opacity: 0;
	transform: translateY(-1rem);
}

.time-entry-show-enter-to {
	opacity: 1;
	transform: translateY(0);
}
</style>
